import React, { useState } from 'react'
import type { TFunction } from 'i18next'
import type { Building } from '@popety-io/types'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import { AnalysisTabButtonMenu } from '../../molecules/AnalysisTabButtonMenu'
import {
  AnalysisTabContentContainer,
  SectionAnalysisContentContainer,
} from '../../molecules/SectionAnalysisStyles'
import { Container, Grid } from './EnergyTab.styles'

// Import components
import { DataSourceBanner } from './components/DataSourceBanner'
import { HeatingSystemsSection } from './components/HeatingSystemsSection'
import { SolarInstallationSection } from './components/SolarInstallationSection'
import { SolarComparisonCard } from './components/SolarComparisonCard'
import { IDCTab } from './IDCTab'

export interface EnergyTabProps {
  building: Building
  loading?: boolean
  error?: string | null
  className?: string
  t: TFunction
  onFetchIDCComparisons?: (buildingId: string) => Promise<any>
}

export const EnergyTab: React.FC<EnergyTabProps> = ({
  building,
  loading = false,
  error = null,
  className,
  t,
  onFetchIDCComparisons,
}) => {
  const [activeTab, setActiveTab] = useState('heating')

  if (loading) {
    return (
      <Container className={className}>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          {t('states.loading')}
        </div>
      </Container>
    )
  }

  if (error) {
    return (
      <Container className={className}>
        <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
          {t('states.error', { error })}
        </div>
      </Container>
    )
  }

  if (!building) {
    return (
      <Container className={className}>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          {t('states.noData')}
        </div>
      </Container>
    )
  }

  const {
    solar_installed_power,
    photovoltaic_history,
    solar_electrical_efficiency,
    solar_area,
    region_name,
    building_year,
  } = building

  // Check if building has solar data
  const hasSolarInstallation =
    solar_installed_power && solar_installed_power > 0
  const hasPhotovoltaicHistory =
    photovoltaic_history && photovoltaic_history.length > 0
  const hasSolarPotential = solar_electrical_efficiency || solar_area

  // Check if building has IDC data (Geneva only)
  const hasIDC =
    region_name === 'Genève' && building.heat_consumption_index != null

  // Build tabs array
  const tabs = [{ id: 'heating', label: t('tabs.heating'), icon: 'fire' }]

  // Add Solar tab if any solar data exists
  if (hasSolarPotential || hasSolarInstallation || hasPhotovoltaicHistory) {
    tabs.push({ id: 'solar', label: t('tabs.solar'), icon: 'sun' })
  }

  // Add IDC tab conditionally for Geneva buildings
  if (hasIDC) {
    tabs.push({ id: 'idc', label: t('tabs.idc'), icon: 'flame' })
  }

  return (
    <Container className={className}>
      {/* Header */}
      <AnalysisSectionHeader
        title={t('header.title')}
        subtitle={t('header.subtitle')}
      />

      {/* TAB MENU */}
      <AnalysisTabButtonMenu
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId)}
      />

      <AnalysisTabContentContainer>
        {/* TAB CONTENT */}
        <SectionAnalysisContentContainer>
          {/* TAB 1: HEATING */}
          {activeTab === 'heating' && (
            <>
              <Grid $columns={1}>
                {/* Heating Systems Section */}
                <HeatingSystemsSection building={building} t={t} />

                {/* Solar Installation Section (compact mode) */}
                {(hasSolarInstallation || hasPhotovoltaicHistory) && (
                  <SolarInstallationSection building={building} t={t} compact />
                )}

                <DataSourceBanner egid={building.egid} t={t} />
              </Grid>
            </>
          )}

          {/* TAB 2: SOLAR ENERGY */}
          {activeTab === 'solar' && (
            <Grid $columns={1}>
              {/* Solar Installation Section (full mode) */}
              <SolarInstallationSection building={building} t={t} />

              {/* Solar Comparison Card (Actual vs Optimal) */}
              {hasSolarPotential && (
                <SolarComparisonCard building={building} t={t} />
              )}
            </Grid>
          )}

          {/* TAB 3: IDC (Geneva only) */}
          {activeTab === 'idc' && hasIDC && onFetchIDCComparisons && (
            <IDCTab
              buildingId={String(building.id)}
              idc={building.heat_consumption_index || 0}
              buildingYear={building_year}
              t={t}
              onFetchIDCComparisons={onFetchIDCComparisons}
            />
          )}
        </SectionAnalysisContentContainer>
      </AnalysisTabContentContainer>
    </Container>
  )
}

export default EnergyTab
